// Copyright (c) 2021 WiseTime. All rights reserved.

import { concat, Observable, of } from "rxjs"
import { Action } from "redux"
import { catchError, filter, mergeMap } from "rxjs/operators"
import * as TagsSlice from "../slice"
import * as TagActions from "../actions"
import { UpdateTagRequestSchema } from "generated/legebill/site/tag/v4/tag_pb"
import { t } from "@lingui/macro"
import { showSnackBar } from "../../snackbar/actions"
import { Code, executorMutation, Init, isConnectError, tagService } from "../../../app/rpc"
import { create } from "@bufbuild/protobuf"
import { Tag } from "../../../generated/legebill/type/tag_pb"

type UpdateTagRpc = (request: Init<typeof UpdateTagRequestSchema>) => Observable<Tag>

export const updateTag = (updateTagRpc: UpdateTagRpc) =>
  (action$: Observable<Action>): Observable<Action> =>
    action$.pipe(
      filter(TagActions.updateTag.match),
      mergeMap(({ payload }) => updateTagRpc(payload)
        .pipe(
          mergeMap(result => concat(
            of(TagsSlice.updateTag({
              tagId: result.id,
              name: result.name,
              description: result.description,
              suggestionConfig: result.suggestionConfig,
            })),
            of(TagsSlice.setDialogStatus("CLOSED")),
          )),
          catchError(e => handleError(e, payload.id ?? ""))
        ),
      )
    )

const handleError = (e: unknown, tagId: string) => {
  if (isConnectError(e)) {
    const { code } = e
    if (code === Code.NotFound) {
      return concat(
        of(showSnackBar({
          message: t({
            id: "msg_get_tag_not_found",
            message: "Tag not found. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com."
          }),
          messageType: "ERROR",
        })),
        of(TagsSlice.setDialogStatus("CLOSED")),
        of(TagsSlice.removeFromCache(tagId))
      )
    }

    if (code === Code.PermissionDenied) {
      return concat(
        of(showSnackBar({
          message: t({
            id: "msg_update_tag_rpc_permission_denied",
            message: "Failed to edit tag. You do not have permission to edit tags."
          }),
          messageType: "ERROR"
        })),
        of(TagsSlice.setDialogStatus("CLOSED"))
      )
    }

    if (code === Code.AlreadyExists) {
      return concat(
        of(showSnackBar({
          message: t({
            id: "msg_update_tag_rpc_already_exists",
            message: "Failed to edit tag. Tag with same name already exists."
          }),
          messageType: "ERROR"
        })),
        of(TagsSlice.setDialogStatus("CLOSED"))
      )
    }
  }

  return concat(
    of(showSnackBar({
      message: t({
        id: "msg_update_tag_rpc_failed",
        message: "Failed to update tag. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com."
      }),
      messageType: "ERROR",
    })),
    of(TagsSlice.setDialogStatus("ERROR")),
  )
}

const updateTagRpc = (request: Init<typeof UpdateTagRequestSchema>): Observable<Tag> =>
  executorMutation(() => tagService.updateTag(create(UpdateTagRequestSchema, request)))

export default updateTag(updateTagRpc)
